/**
 * Economics Calculations
 *
 * Core module for calculating:
 * - Hardware costs
 * - Power consumption and energy costs
 * - Long-term TCO (Total Cost of Ownership)
 * - Cost per usable TB
 *
 * Reference: DESIGN.md §6, Math Verification Plan §2
 */

import type {
  StorageTopology,
  DriveParameters,
  EconomicsParameters,
  EconomicsResults,
  TCOProjection,
  CapacityResults,
} from '../types';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Hours in a year */
export const HOURS_PER_YEAR = 24 * 365;

/** Default projection years for TCO */
export const DEFAULT_TCO_YEARS = 5;

// ============================================================================
// HARDWARE COST CALCULATIONS
// ============================================================================

/**
 * Calculate total hardware cost
 *
 * Formula: total_drive_cost = drive_cost × total_drives
 *
 * @param topology - Storage topology
 * @param driveCost - Cost per drive in USD
 * @returns Total hardware cost in USD
 */
export function calculateHardwareCost(
  topology: StorageTopology,
  driveCost: number
): number {
  const totalDrives = topology.drivesPerVdev * topology.vdevCount;
  return totalDrives * driveCost;
}

/**
 * Calculate cost per usable TB
 *
 * Formula: cost_per_usable_tb = total_drive_cost / final_usable_tb
 *
 * @param hardwareCost - Total hardware cost in USD
 * @param usableTiB - Final usable capacity in TiB
 * @returns Cost per usable TB in USD
 */
export function calculateCostPerUsableTB(
  hardwareCost: number,
  usableTiB: number
): number {
  const usableTB = usableTiB / Math.pow(1000 / 1024, 4);
  return hardwareCost / usableTB;
}

// ============================================================================
// POWER CONSUMPTION CALCULATIONS
// ============================================================================

/**
 * Calculate total power consumption
 *
 * @param topology - Storage topology
 * @param wattsIdle - Idle power per drive
 * @param wattsLoad - Load power per drive
 * @returns Object with total watts (idle and load)
 */
export function calculateTotalPower(
  topology: StorageTopology,
  wattsIdle: number,
  wattsLoad: number
): { totalWattsIdle: number; totalWattsLoad: number } {
  const totalDrives = topology.drivesPerVdev * topology.vdevCount;
  return {
    totalWattsIdle: totalDrives * wattsIdle,
    totalWattsLoad: totalDrives * wattsLoad,
  };
}

/**
 * Calculate annual energy consumption
 *
 * Formula: kWh_per_year = (total_watts × hours_per_year) / 1000
 *
 * @param totalWatts - Total power consumption in watts
 * @param uptimeHoursPerDay - Hours per day system is running
 * @returns Annual energy consumption in kWh
 */
export function calculateAnnualKWh(
  totalWatts: number,
  uptimeHoursPerDay: number
): number {
  const hoursPerYear = uptimeHoursPerDay * 365;
  return (totalWatts * hoursPerYear) / 1000;
}

/**
 * Calculate annual power cost
 *
 * Formula: annual_cost = kWh_per_year × electricity_rate
 *
 * @param annualKWh - Annual energy consumption in kWh
 * @param electricityRate - Cost per kWh in USD
 * @returns Annual power cost in USD
 */
export function calculateAnnualPowerCost(
  annualKWh: number,
  electricityRate: number
): number {
  return annualKWh * electricityRate;
}

// ============================================================================
// LONG-TERM TCO CALCULATIONS
// ============================================================================

/**
 * Calculate expected drive failures per year
 *
 * Uses AFR (Annual Failure Rate) to estimate expected failures.
 *
 * Formula: expected_failures = total_drives × AFR
 *
 * @param totalDrives - Total number of drives
 * @param afr - Annual Failure Rate as percentage (e.g., 1.5 for 1.5%)
 * @returns Expected number of drive failures per year
 */
export function calculateExpectedFailures(
  totalDrives: number,
  afr: number
): number {
  return totalDrives * (afr / 100);
}

/**
 * Calculate TCO projection over multiple years
 *
 * Models cumulative costs including:
 * - Initial hardware cost
 * - Annual power costs
 * - Expected replacement costs (probability-based)
 *
 * @param topology - Storage topology
 * @param drive - Drive parameters
 * @param economics - Economics parameters
 * @param years - Number of years to project (default 5)
 * @returns Array of annual TCO projections
 */
export function calculateTCOProjection(
  topology: StorageTopology,
  drive: DriveParameters,
  economics: EconomicsParameters,
  years: number = DEFAULT_TCO_YEARS
): TCOProjection[] {
  const projections: TCOProjection[] = [];

  const totalDrives = topology.drivesPerVdev * topology.vdevCount;
  const hardwareCost = calculateHardwareCost(topology, drive.driveCost);
  const { totalWattsIdle } = calculateTotalPower(
    topology,
    drive.wattsIdle,
    drive.wattsLoad
  );
  const annualKWh = calculateAnnualKWh(totalWattsIdle, economics.uptimeHoursPerDay);
  const annualPowerCost = calculateAnnualPowerCost(annualKWh, economics.electricityRate);

  let cumulativeCost = hardwareCost;

  for (let year = 1; year <= years; year++) {
    // Expected failures this year
    const expectedFailures = calculateExpectedFailures(totalDrives, drive.afr);

    // Replacement cost (assume drives need replacement, but not all fail)
    // Use expected failures as average case
    const replacementCost = expectedFailures * drive.driveCost;

    // Power cost this year
    const powerCost = annualPowerCost;

    // Cumulative cost
    cumulativeCost += replacementCost + powerCost;

    projections.push({
      year,
      expectedFailures,
      replacementCost,
      powerCost,
      cumulativeCost,
    });
  }

  return projections;
}

// ============================================================================
// MAIN ECONOMICS CALCULATION ORCHESTRATOR
// ============================================================================

/**
 * Calculate complete economics assessment
 *
 * Orchestrates all economics calculations:
 * 1. Hardware costs
 * 2. Cost per usable TB
 * 3. Power consumption
 * 4. Annual energy costs
 * 5. 5-year TCO projection
 *
 * @param topology - Storage topology
 * @param drive - Drive parameters
 * @param economics - Economics parameters
 * @param capacity - Capacity results (for usable TiB)
 * @returns Complete economics assessment
 */
export function calculateEconomics(
  topology: StorageTopology,
  drive: DriveParameters,
  economics: EconomicsParameters,
  capacity: CapacityResults
): EconomicsResults {
  const totalDrives = topology.drivesPerVdev * topology.vdevCount;

  // Hardware costs
  const hardwareCost = calculateHardwareCost(topology, drive.driveCost);
  const costPerUsableTB = calculateCostPerUsableTB(
    hardwareCost,
    capacity.finalUsableTiB
  );

  // Power consumption
  const { totalWattsIdle, totalWattsLoad } = calculateTotalPower(
    topology,
    drive.wattsIdle,
    drive.wattsLoad
  );

  // Annual energy
  const annualKWh = calculateAnnualKWh(totalWattsIdle, economics.uptimeHoursPerDay);
  const annualPowerCost = calculateAnnualPowerCost(annualKWh, economics.electricityRate);

  // TCO projection
  const tcoProjection = calculateTCOProjection(
    topology,
    drive,
    economics,
    DEFAULT_TCO_YEARS
  );

  return {
    totalDrives,
    hardwareCost,
    costPerUsableTB,
    totalWattsIdle,
    totalWattsLoad,
    annualKWh,
    annualPowerCost,
    tcoProjection,
  };
}
